import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'

import {
  ERROR_MESSAGES,
  SUCCESS_MESSAGES
} from '@/constants/common/message.constants'
import { toastApiErrorMessage } from '@/features/shared/utils'
import { type ShipmentOrdersListExEntity } from '@/libs/api'

import { createShipmentBundle } from '../api'
import { canBundleOrders, collectBundleDateOptions } from '../models'

/** 束ね作成時に全依頼へ揃える日付（日付統一モーダルの確定値） */
type BundleDates = {
  shippingDate?: string
  deliveryDate?: string
  incomeRecordingDateHandling?: 'FOLLOW' | 'ALIGN' | 'KEEP'
  incomeRecordingDate?: string
}

type Params = {
  tenantKey: string
  /** 束ねの対象に選択されている配送依頼 */
  selectedShipmentOrders: ShipmentOrdersListExEntity[]
  /** 束ね成功後の画面ごとの後処理（一覧の再取得・選択の解除など） */
  onBundled: () => void | Promise<void>
}

/**
 * 「複数依頼をまとめる」の共通フロー（配送依頼一覧と配車板の配送依頼ドロワーで共用）。
 * 発日・着日が複数日付に割れている選択では日付統一モーダルを挟んでから束ね、
 * 揃っている選択ではそのまま束ねる。モーダル自体
 * ({@link ShipmentOrderBundleDateAlignModal}) の描画は呼び出し元が行う
 * （ドロワー配下では zIndex 指定が要るため）。
 */
export const useCreateShipmentBundle = ({
  tenantKey,
  selectedShipmentOrders,
  onBundled
}: Params) => {
  const [bundling, setBundling] = useState(false)
  const [dateAlignModalOpen, setDateAlignModalOpen] = useState(false)

  const { canBundle } = canBundleOrders(selectedShipmentOrders)

  // 選択行の発日・着日が複数日付に割れているか（割れていれば束ね前に統一を挟む）
  const { needsAlignment: needsDateAlignment } = useMemo(
    () => collectBundleDateOptions(selectedShipmentOrders),
    [selectedShipmentOrders]
  )

  const bundleShipmentOrders = async (dates?: BundleDates) => {
    setBundling(true)
    try {
      await createShipmentBundle({
        tenantKey,
        shipmentOrderIds: selectedShipmentOrders.map((order) => order.id),
        ...dates
      })
      await onBundled()
      setDateAlignModalOpen(false)
      toast.success(SUCCESS_MESSAGES.BUNDLED_SHIPMENT_ORDERS)
    } catch (error) {
      toastApiErrorMessage(
        error,
        ERROR_MESSAGES.FAILED_TO_BUNDLE_SHIPMENT_ORDERS
      )
    } finally {
      setBundling(false)
    }
  }

  const startBundleShipmentOrders = () => {
    // 発日・着日のどちらかが複数日付になる場合は、揃える日付を選ばせてから束ねる
    if (needsDateAlignment) {
      setDateAlignModalOpen(true)
      return
    }
    void bundleShipmentOrders()
  }

  return {
    /** 選択中の依頼をまとめられるか（判定規則は canBundleOrders 参照） */
    canBundle,
    /** 束ね作成の実行中 */
    bundling,
    dateAlignModalOpen,
    closeDateAlignModal: () => setDateAlignModalOpen(false),
    /** 「複数依頼をまとめる」ボタンの押下起点。必要なら日付統一モーダルを開く */
    startBundleShipmentOrders,
    /** 束ねの実行本体。日付統一モーダルの onConfirm からも呼ばれる */
    bundleShipmentOrders
  }
}
